export const queryKeys = {
  auth: {
    me: () => ['auth', 'me'] as const,
    validateResetToken: (token: string) =>
      ['auth', 'validate-reset-token', token] as const,
  },
  companies: {
    all: (tenantId: string) => ['companies', tenantId] as const,
    list: (tenantId: string, filters?: Record<string, unknown>) =>
      ['companies', tenantId, 'list', filters] as const,
    detail: (tenantId: string, id: string) =>
      ['companies', tenantId, 'detail', id] as const,
  },
  campaigns: {
    all: (tenantId: string) => ['campaigns', tenantId] as const,
    list: (tenantId: string, filters?: Record<string, unknown>) =>
      ['campaigns', tenantId, 'list', filters] as const,
    detail: (tenantId: string, id: string) =>
      ['campaigns', tenantId, 'detail', id] as const,
    instruments: (tenantId: string, campaignId: string) =>
      ['campaigns', tenantId, 'instruments', campaignId] as const,
  },
  instrumentDashboard: {
    all: (tenantId: string) => ['instrument-dashboard', tenantId] as const,
    summary: (
      tenantId: string,
      instrumentId: string,
      filters?: Record<string, string>,
    ) => ['instrument-dashboard', tenantId, instrumentId, 'summary', filters ?? {}] as const,
  },
  questionDashboard: {
    all: (tenantId: string) => ['question-dashboard', tenantId] as const,
    detail: (tenantId: string, instrumentId: string, questionId: string) =>
      ['question-dashboard', tenantId, instrumentId, questionId] as const,
  },
  loiDashboard: {
    all: (tenantId: string) => ['loi-dashboard', tenantId] as const,
    summary: (tenantId: string, loiId: string) =>
      ['loi-dashboard', tenantId, loiId, 'summary'] as const,
    instruments: (tenantId: string, loiId: string, status: string) =>
      ['loi-dashboard', tenantId, loiId, 'instruments', status] as const,
    burndown: (tenantId: string, loiId: string, granularity: string) =>
      ['loi-dashboard', tenantId, loiId, 'burndown', granularity] as const,
    topAnswers: (tenantId: string, loiId: string) =>
      ['loi-dashboard', tenantId, loiId, 'top-answers'] as const,
    insights: (tenantId: string, loiId: string) =>
      ['loi-dashboard', tenantId, loiId, 'insights'] as const,
    recommendations: (tenantId: string, loiId: string) =>
      ['loi-dashboard', tenantId, loiId, 'recommendations'] as const,
    teamNotes: (tenantId: string, loiId: string) =>
      ['loi-dashboard', tenantId, loiId, 'team-notes'] as const,
  },
  insights: {
    instrument: (tenantId: string, campaignId: string, instrumentId: string) =>
      ['insights', tenantId, campaignId, instrumentId] as const,
    recommendation: (tenantId: string, campaignId: string, instrumentId: string) =>
      ['insights', tenantId, campaignId, instrumentId, 'recommendation'] as const,
    question: (tenantId: string, instrumentId: string, questionId: string) =>
      ['insights', tenantId, instrumentId, questionId] as const,
    questionRecommendation: (
      tenantId: string,
      instrumentId: string,
      questionId: string,
      insightsRevision: string,
    ) =>
      [
        'insights',
        tenantId,
        instrumentId,
        questionId,
        'recommendation',
        insightsRevision,
      ] as const,
  },
  tenants: {
    all: () => ['tenants'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['tenants', 'list', filters] as const,
    detail: (id: string) => ['tenants', 'detail', id] as const,
  },
  signalProviders: {
    all: () => ['signal-providers'] as const,
    list: () => ['signal-providers', 'list'] as const,
    detail: (platformUserId: string) =>
      ['signal-providers', 'detail', platformUserId] as const,
  },
  users: {
    all: (tenantId: string) => ['users', tenantId] as const,
    list: (tenantId: string, filters?: Record<string, unknown>) =>
      ['users', tenantId, 'list', filters] as const,
    detail: (tenantId: string, id: string) =>
      ['users', tenantId, 'detail', id] as const,
  },
  instruments: {
    all: (tenantId: string) => ['instruments', tenantId] as const,
    list: (tenantId: string, filters?: Record<string, unknown>) =>
      ['instruments', tenantId, 'list', filters] as const,
    detail: (tenantId: string, id: string) =>
      ['instruments', tenantId, 'detail', id] as const,
    /** Builder / routing draft payload (`GET …/instruments/:id/draft`). */
    draft: (instrumentId: string) => ['instruments', 'draft', instrumentId] as const,
  },
  team: {
    all: (tenantId: string) => ['team', tenantId] as const,
    activeMembers: (tenantId: string) =>
      ['team', tenantId, 'active-members'] as const,
    members: (p: {
      page: number
      pageSize: number
      role: string
      status: string
      search: string
      sortBy: string
      sortOrder: string
    }) =>
      [
        'team',
        'members',
        p.page,
        p.pageSize,
        p.role,
        p.status,
        p.search,
        p.sortBy,
        p.sortOrder,
      ] as const,
    member: (id: string) => ['team', 'member', id] as const,
    invitationValidate: (token: string) =>
      ['team', 'invitation', 'validate', token] as const,
  },
  adminDashboard: {
    metrics: (tenantId: string) => ['admin-dashboard', tenantId, 'metrics'] as const,
  },
  notifications: {
    all: () => ['notifications'] as const,
    /** Paginated bell list — infinite query (incremental load). */
    infinite: () => ['notifications', 'infinite'] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },
  emailChange: {
    all: () => ['email-change'] as const,
    /** `visitKey` — React Router `location.key` (one send per navigation). */
    currentSend: (visitKey: string) =>
      [...queryKeys.emailChange.all(), 'current-send', visitKey] as const,
  },
  platformDashboard: {
    all: () => ['platform-dashboard'] as const,
    peopleAccounts: (growthPeriod: 'week' | 'month') =>
      [...queryKeys.platformDashboard.all(), 'people-accounts', growthPeriod] as const,
  },
  reports: {
    all: (tenantId: string) => ['reports', tenantId] as const,
    instruments: (tenantId: string) =>
      ['reports', tenantId, 'instruments'] as const,
    lois: (tenantId: string) => ['reports', tenantId, 'lois'] as const,
  },
} as const
